import React from 'react';
import { relativeTime } from '../domainMetrics';
import { money } from '../earnings';

/* Fleet totals over whatever the filters leave in the table, plus a
   freshness line so a stale number is never mistaken for a current one. */

const Kpi = ({ label, value, hint, accent }) => (
  <div className="kpi" title={hint}>
    <div className="kpi-label">{label}</div>
    <div className="kpi-value" style={accent ? { color: accent } : undefined}>{value}</div>
  </div>
);

const freshness = (label, iso, now) => {
  const rel = relativeTime(iso, now);
  return `${label} ${rel || 'never'}`;
};

export default function KpiStrip({ kpis, updatedAt = {}, earnings = null }) {
  const now = new Date();
  const label = earnings && earnings.period
    ? earnings.period.label.replace(' (Amazon report)', '') : '';
  return (
    <div style={{ marginBottom: '20px' }}>
      <div className="kpi-grid" style={{ marginBottom: '6px' }}>
        {earnings ? (
          <Kpi
            label={`Total earnings · ${label}`}
            value={`${money(earnings.portfolio.total)}${earnings.partial ? '*' : ''}`}
            hint={`AdSense ${money(earnings.portfolio.adsense)} + Amazon ${money(earnings.portfolio.amazon)}`
              + ' over the sites shown (Amazon includes earnings not attributed to any site).'
              + (earnings.partial ? ' * Partial: '
                + [!earnings.adsensePeriod.available && earnings.adsensePeriod.note,
                  !earnings.amazonPeriod.available && earnings.amazonPeriod.note].filter(Boolean).join('; ') : '')}
          />
        ) : (
          <Kpi
            label="Earnings (30d)"
            value={'$' + kpis.earnings.toFixed(2)}
            hint="AdSense estimated earnings, last 30 complete days, over the sites shown"
          />
        )}
        {earnings && (
          <Kpi
            label="AdSense · Amazon"
            value={`${money(earnings.portfolio.adsense)} · ${money(earnings.portfolio.amazon)}`}
            hint={`${label}. A dash means that source has no data covering the period.`}
          />
        )}
        <Kpi
          label="Hits (30d)"
          value={kpis.hits.toLocaleString()}
          hint="Page views, last 30 complete MST days, over the sites shown"
        />
        <Kpi
          label="eCPM"
          value={kpis.ecpm != null ? '$' + kpis.ecpm.toFixed(2) : '—'}
          hint="Earnings per 1,000 ad impressions, averaged over ad-tracked sites only"
        />
        <Kpi
          label="RPM"
          value={kpis.rpm != null ? '$' + kpis.rpm.toFixed(2) : '—'}
          hint="AdSense earnings per 1,000 page views (30d), averaged over sites with page views"
        />
        <Kpi
          label="Scheduled"
          value={kpis.scheduled.toLocaleString()}
          hint="Published pages with a future published_at, plus drafts the daily publish queue has dated"
          accent={kpis.scheduled > 0 ? '#0c5460' : undefined}
        />
        <Kpi
          label="New this month"
          value={kpis.newThisMonth.toLocaleString()}
          hint="Pages created since the 1st"
          accent={kpis.newThisMonth > 0 ? '#155724' : undefined}
        />
        <Kpi
          label="Drafts"
          value={kpis.drafts.toLocaleString()}
          hint="Pages in draft (or with no status) — refresh page stats to update"
          accent={kpis.drafts > 0 ? '#856404' : undefined}
        />
      </div>
      <div className="muted small" title="When each analytics source was last pulled from its upstream">
        {freshness('Hits updated', updatedAt.hits, now)}
        {' · '}
        {freshness('Earnings', updatedAt.earnings, now)}
        {' · '}
        {freshness('Impressions', updatedAt.impressions, now)}
        {earnings && <>{' · '}{freshness('Amazon report uploaded', updatedAt.amazon, now)}</>}
      </div>
    </div>
  );
}
